import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CampusLockUp } from './Forms/campus-lock-up';
import { EquipmentRequest } from './Forms/equipment-request';
import { PlatformChangeRequestForm } from './Forms/platform-change-request';
import { LiveServicesTechRequestForm } from './Forms/LiveServiceTechRequest';
import { PerformingArtsTechRequestForm } from './Forms/PerformArtsTechRequest';
import { ParseAPI } from '../API';
import { useAuth } from './AuthContext';
import {
    ApprovalRail,
    Banner,
    Button,
    Card,
    LoadingOverlay,
    PageBody,
    PageHeader,
    StatusBadge,
    cx,
    deriveStatus,
} from './ui';

const SERVICES = [
    { name: 'campusLockUp', label: 'Campus lock-up', hint: 'Someone must secure the campus afterwards' },
    { name: 'equipmentRequest', label: 'Equipment', hint: 'Tables, chairs, projector, TV' },
    { name: 'platformChangeRequest', label: 'Platform change', hint: 'Stage, risers, curtains' },
    { name: 'liveServicesTechRequest', label: 'Live services / performing arts tech', hint: 'Sound, video, lighting' },
    { name: 'securityPersonnel', label: 'Security personnel', hint: 'Guards on site' },
    { name: 'sprinklerAdjustment', label: 'Sprinkler adjustment', hint: 'Irrigation off during the event' },
    { name: 'trafficConeAdjustment', label: 'Traffic cones', hint: 'Parking or traffic changes' },
];

const SLOS = [
    { name: 'criticalThinkers', label: 'Critical & creative thinkers' },
    { name: 'competentCommunicators', label: 'Competent communicators' },
    { name: 'communityContributors', label: 'Community contributors' },
    { name: 'lifeLongLearners', label: 'Life-long learners' },
    { name: 'christCentered', label: 'Christ-centered' },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Local YYYY-MM-DD. toISOString() would convert to UTC and can land the
// occurrence on the wrong day.
const toISODate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const parseISODate = (value) => new Date(`${value}T00:00:00`);

const formatEventDate = (value) =>
    value ? parseISODate(value).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';

const formatChipDate = (value) =>
    value ? parseISODate(value).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : '';

const Field = ({ label, htmlFor, hint, className, children }) => (
    <div className={className}>
        <div className="flex items-baseline justify-between gap-2">
            <label htmlFor={htmlFor} className="block text-sm font-medium text-neutral-800">
                {label}
            </label>
            {hint}
        </div>
        <div className="mt-1.5">{children}</div>
    </div>
);

const Section = ({ id, title, description, children }) => (
    <Card id={id} className="scroll-mt-24 p-6">
        <div className="mb-5">
            <h2 className="font-display text-lg font-semibold text-neutral-900">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-neutral-500">{description}</p>}
        </div>
        {children}
    </Card>
);

const Check = ({ name, label, hint, checked, onChange, disabled }) => (
    <label
        className={cx(
            'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
            checked ? 'border-brand-200 bg-brand-50/60' : 'border-neutral-200 hover:bg-neutral-50',
            disabled && 'cursor-not-allowed opacity-70'
        )}
    >
        <input
            type="checkbox"
            name={name}
            id={name}
            checked={!!checked}
            onChange={onChange}
            disabled={disabled}
            className="mt-0.5"
        />
        <span className="min-w-0">
            <span className="block text-sm font-medium text-neutral-800">{label}</span>
            {hint && <span className="mt-0.5 block text-xs text-neutral-500">{hint}</span>}
        </span>
    </label>
);

export const MasterCalendarForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const platformRef = useRef(null);

    const [formData, setFormData] = useState({
        eventName: '',
        department: '',
        dayDateOfEvent: '',
        startTime: '',
        endTime: '',
        prepTime: '',
        locationRequested: '',
        estimatedAttendance: '',
        eventOverseer: '',
        criticalThinkers: false,
        competentCommunicators: false,
        communityContributors: false,
        lifeLongLearners: false,
        christCentered: false,
        sloExplanation: '',
        additionalDetails: '',
        campusLockUp: false,
        equipmentRequest: false,
        platformChangeRequest: false,
        liveServicesTechRequest: false,
        securityPersonnel: false,
        sprinklerAdjustment: false,
        trafficConeAdjustment: false,
        campusLockUpForm: {},
        equipmentRequestForm: {},
        platformChangeRequestForm: {},
        liveServicesTechRequestForm: {},
        performingArtsTechRequestForm: {},
        isRecurring: false,
        recurrenceType: 'weekly',
        recurrenceEndDate: '',
        recurrenceDays: [],
        recurrenceInterval: 1,
        // Dates the pattern produces but this event skips (holidays, clashes).
        skippedDates: [],
        // Every date this one request covers. The calendar expands these.
        occurrenceDates: [],
    });

    const [loading, setLoading] = useState(false);
    const [busyMessage, setBusyMessage] = useState('Saving…');
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    /* ---------------------------------------------------------------- load */

    useEffect(() => {
        const duplicated = localStorage.getItem('duplicatedFormData');

        if (!id && duplicated) {
            try {
                const parsed = JSON.parse(duplicated);
                setFormData((prev) => ({
                    ...prev,
                    ...parsed,
                    // A copy is a brand new request: it owns none of the original's state.
                    objectId: undefined,
                    isDraft: undefined,
                    isFullyApproved: undefined,
                    isDenied: undefined,
                    isCancelled: undefined,
                    requiredApprovals: [],
                    completedApprovals: [],
                    dayDateOfEvent: '',
                    startTime: '',
                    endTime: '',
                    prepTime: '',
                    campusLockUpForm: parsed.campusLockUpForm || {},
                    equipmentRequestForm: parsed.equipmentRequestForm || {},
                    platformChangeRequestForm: parsed.platformChangeRequestForm || {},
                    liveServicesTechRequestForm: parsed.liveServicesTechRequestForm || {},
                    performingArtsTechRequestForm: parsed.performingArtsTechRequestForm || {},
                }));
            } catch (err) {
                console.error('Could not read the copied request:', err);
            } finally {
                localStorage.removeItem('duplicatedFormData');
            }
            return;
        }

        if (!id) return;

        (async () => {
            setBusyMessage('Loading request…');
            setLoading(true);
            try {
                const formObject = await ParseAPI.getFormById(id);
                if (!formObject) throw new Error('This request no longer exists.');

                const data = formObject.toJSON();
                setFormData((prev) => ({
                    ...prev,
                    ...data,
                    campusLockUpForm: data.campusLockUpForm || {},
                    equipmentRequestForm: data.equipmentRequestForm || {},
                    platformChangeRequestForm: data.platformChangeRequestForm || {},
                    liveServicesTechRequestForm: data.liveServicesTechRequestForm || {},
                    performingArtsTechRequestForm: data.performingArtsTechRequestForm || {},
                    recurrenceDays: data.recurrenceDays || [],
                    skippedDates: data.skippedDates || [],
                    occurrenceDates: data.occurrenceDates || [],
                }));
            } catch (err) {
                console.error('Could not load the request:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    /* --------------------------------------------------------------- state */

    const status = id ? deriveStatus(formData) : 'Draft';
    const isNew = !id;
    const isOwner = !formData.owner || formData.owner === user?.email;
    const isCancelled = !!formData.isCancelled;

    // Owners can edit their own request at any point in its life. Editing a
    // submitted one is allowed on purpose — saving restarts the approvals.
    const isViewOnly = (!isNew && !isOwner) || isCancelled;
    const isSubmitted = !isNew && !formData.isDraft && !isCancelled;
    const willRestartApprovals = isSubmitted && !isViewOnly;

    const handleChange = (event) => {
        const { name, type, value, checked } = event.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleUpdate = (childName, childData) =>
        setFormData((prev) => ({ ...prev, [childName]: childData }));

    const toggleRecurrenceDay = (dayIndex, checked) =>
        setFormData((prev) => ({
            ...prev,
            recurrenceDays: (checked
                ? [...prev.recurrenceDays, dayIndex]
                : prev.recurrenceDays.filter((d) => d !== dayIndex)
            ).sort(),
        }));

    /* ---------------------------------------------------------- recurrence */

    // A recurring event is ONE request that happens on several dates — not one
    // request per date. It is approved once, and the calendar expands it.
    const patternDates = useMemo(() => {
        if (!formData.isRecurring || !formData.dayDateOfEvent || !formData.recurrenceEndDate) {
            return [];
        }

        const dates = [];
        const start = new Date(`${formData.dayDateOfEvent}T00:00:00`);
        const end = new Date(`${formData.recurrenceEndDate}T00:00:00`);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];

        const step = Math.max(1, parseInt(formData.recurrenceInterval, 10) || 1);
        const cursor = new Date(start);

        // Guard against a bad interval producing a runaway list.
        while (cursor <= end && dates.length < 366) {
            if (formData.recurrenceType === 'weekly') {
                const days = formData.recurrenceDays.length ? formData.recurrenceDays : [start.getDay()];
                if (days.includes(cursor.getDay())) dates.push(toISODate(cursor));
                cursor.setDate(cursor.getDate() + 1);
            } else if (formData.recurrenceType === 'monthly') {
                dates.push(toISODate(cursor));
                cursor.setMonth(cursor.getMonth() + step);
            } else {
                dates.push(toISODate(cursor));
                cursor.setDate(cursor.getDate() + step);
            }
        }
        return dates;
    }, [
        formData.isRecurring,
        formData.dayDateOfEvent,
        formData.recurrenceEndDate,
        formData.recurrenceType,
        formData.recurrenceInterval,
        formData.recurrenceDays,
    ]);

    // Holidays and one-off clashes: drop a single date without abandoning the
    // pattern or filing a separate request.
    const occurrences = useMemo(
        () => patternDates.filter((date) => !(formData.skippedDates || []).includes(date)),
        [patternDates, formData.skippedDates]
    );

    const toggleSkippedDate = (date) =>
        setFormData((prev) => {
            const skipped = prev.skippedDates || [];
            return {
                ...prev,
                skippedDates: skipped.includes(date)
                    ? skipped.filter((d) => d !== date)
                    : [...skipped, date].sort(),
            };
        });

    /* --------------------------------------------------------------- save */

    const additionalSignatures = () => {
        const signatures = [];
        if (formData.campusLockUp) signatures.push(5);
        if (formData.securityPersonnel || formData.trafficConeAdjustment) signatures.push(6);
        if (formData.sprinklerAdjustment) signatures.push(7);
        if (formData.platformChangeRequest || formData.liveServicesTechRequest) signatures.push(8);
        return signatures;
    };

    const finish = () => {
        setSaved(true);
        setTimeout(() => navigate('/home/request'), 1200);
    };

    // A recurring request is saved as a single form carrying every date it
    // happens on, so it is approved once rather than once per date.
    const buildPayload = async () => {
        const platformImage = platformRef.current ? await platformRef.current.exportImage() : null;
        const dates = formData.isRecurring ? occurrences : [formData.dayDateOfEvent].filter(Boolean);

        return {
            ...formData,
            platformImage,
            additionalSignatures: additionalSignatures(),
            occurrenceDates: dates,
            // The first date stays in dayDateOfEvent so everything that reads a
            // single date — the PDF, the emails, sorting — keeps working.
            dayDateOfEvent: dates[0] || formData.dayDateOfEvent,
        };
    };

    const handleSubmit = async () => {
        if (formData.isRecurring && !occurrences.length) {
            setError('This repeats, but no dates are left. Choose an end date, or stop skipping every date.');
            return;
        }

        setBusyMessage(willRestartApprovals ? 'Saving changes and restarting approvals…' : 'Submitting request…');
        setLoading(true);
        setError(null);

        try {
            await ParseAPI.saveForm(await buildPayload(), user, false);
            finish();
        } catch (err) {
            console.error('Could not submit the request:', err);
            setError(`Your request could not be submitted: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDraft = async () => {
        setBusyMessage('Saving draft…');
        setLoading(true);
        setError(null);
        try {
            await ParseAPI.saveForm(await buildPayload(), user, true);
            finish();
        } catch (err) {
            console.error('Could not save the draft:', err);
            setError(`Your draft could not be saved: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    /* ------------------------------------------------------------- section nav */

    const sections = [
        { id: 'event', label: 'Event details' },
        { id: 'when', label: 'Date & time' },
        { id: 'repeat', label: 'Repeats' },
        { id: 'slos', label: 'Learning outcomes' },
        { id: 'services', label: 'Additional services' },
        formData.campusLockUp && { id: 'lockup', label: 'Campus lock-up' },
        formData.equipmentRequest && { id: 'equipment', label: 'Equipment' },
        formData.platformChangeRequest && { id: 'platform', label: 'Platform change' },
        formData.liveServicesTechRequest && { id: 'tech', label: 'Live services tech' },
        formData.liveServicesTechRequest && { id: 'arts', label: 'Performing arts tech' },
    ].filter(Boolean);

    /* -------------------------------------------------------------- render */

    return (
        <>
            {loading && <LoadingOverlay message={busyMessage} />}
            {saved && <LoadingOverlay message="Saved. Taking you back to your requests…" />}

            <PageHeader
                title={isNew ? 'New event request' : formData.eventName || 'Event request'}
                description={
                    isViewOnly
                        ? 'You can read this request, but only its owner can change it.'
                        : 'On-campus event request. Everything here ends up on the printed form.'
                }
                actions={
                    !isNew && (
                        <div className="flex items-center gap-3">
                            <StatusBadge status={status} />
                            {(formData.requiredApprovals || []).length > 0 && (
                                <div className="hidden md:block">
                                    <ApprovalRail
                                        required={formData.requiredApprovals}
                                        completed={formData.completedApprovals}
                                        denied={status === 'Denied'}
                                    />
                                </div>
                            )}
                        </div>
                    )
                }
            />

            <PageBody>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                    className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-8"
                >
                    {/* Section rail — the long form's table of contents. */}
                    <aside className="mb-6 hidden lg:sticky lg:top-8 lg:mb-0 lg:block lg:self-start">
                        <p className="px-3 pb-2 text-xxs font-semibold uppercase tracking-widest text-neutral-400">
                            Sections
                        </p>
                        <ul className="space-y-0.5">
                            {sections.map((section) => (
                                <li key={section.id}>
                                    <a
                                        href={`#${section.id}`}
                                        className="block rounded-md px-3 py-1.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
                                    >
                                        {section.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </aside>

                    <div className="space-y-5">
                        {error && (
                            <Banner tone="danger" title="Something went wrong">
                                {error}
                            </Banner>
                        )}

                        {isCancelled && (
                            <Banner tone="info" title="This request was cancelled">
                                {formData.cancellationReason || 'No reason was recorded.'}
                            </Banner>
                        )}

                        {status === 'Denied' && (
                            <Banner tone="danger" title="This request was denied">
                                Make the changes you were asked for, then submit again — approvals will start over.
                            </Banner>
                        )}

                        {willRestartApprovals && (
                            <Banner tone="warning" title="Changes restart the approval process">
                                This request has already gone out for approval. Saving replaces the PDF and asks
                                every approver to sign off again.
                            </Banner>
                        )}

                        {formData.convertedFromSaveTheDate && (
                            <Banner tone="info">
                                Built from a Save the Date. Fill in the details it didn't ask for.
                            </Banner>
                        )}

                        <Section id="event" title="Event details" description="Who's running it, and how big it is.">
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
                                <Field label="Event name" htmlFor="eventName" className="sm:col-span-6">
                                    <input
                                        required
                                        type="text"
                                        name="eventName"
                                        id="eventName"
                                        placeholder="First day of school"
                                        value={formData.eventName}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>

                                <Field label="Department" htmlFor="department" className="sm:col-span-3">
                                    <input
                                        required
                                        type="text"
                                        name="department"
                                        id="department"
                                        placeholder="Church, School, Youth…"
                                        value={formData.department}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>

                                <Field label="Estimated attendance" htmlFor="estimatedAttendance" className="sm:col-span-3">
                                    <input
                                        required
                                        type="number"
                                        min="0"
                                        name="estimatedAttendance"
                                        id="estimatedAttendance"
                                        placeholder="0"
                                        value={formData.estimatedAttendance}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>

                                <Field label="Person overseeing the event" htmlFor="eventOverseer" className="sm:col-span-6">
                                    <input
                                        required
                                        type="text"
                                        name="eventOverseer"
                                        id="eventOverseer"
                                        placeholder="Who is on site and responsible"
                                        value={formData.eventOverseer}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section id="when" title="Date & time" description="Include the time you need beforehand to set up.">
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
                                <Field label="Event date" htmlFor="dayDateOfEvent" className="sm:col-span-2">
                                    <input
                                        required
                                        type="date"
                                        name="dayDateOfEvent"
                                        id="dayDateOfEvent"
                                        value={formData.dayDateOfEvent}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>

                                <Field label="Start time" htmlFor="startTime" className="sm:col-span-2">
                                    <input
                                        required
                                        type="time"
                                        name="startTime"
                                        id="startTime"
                                        value={formData.startTime}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>

                                <Field label="End time" htmlFor="endTime" className="sm:col-span-2">
                                    <input
                                        required
                                        type="time"
                                        name="endTime"
                                        id="endTime"
                                        value={formData.endTime}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>

                                <Field label="Time reserved for prep" htmlFor="prepTime" className="sm:col-span-3">
                                    <input
                                        required
                                        type="time"
                                        name="prepTime"
                                        id="prepTime"
                                        value={formData.prepTime}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>

                                <Field
                                    label="Location"
                                    htmlFor="locationRequested"
                                    className="sm:col-span-3"
                                    hint={
                                        <a
                                            href="/campus-map.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs font-medium text-brand-700 hover:text-brand-800"
                                        >
                                            Campus map
                                        </a>
                                    }
                                >
                                    <input
                                        required
                                        type="text"
                                        name="locationRequested"
                                        id="locationRequested"
                                        placeholder="Main Sanctuary"
                                        value={formData.locationRequested}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section
                            id="repeat"
                            title="Repeats"
                            description="One request covering every date the event happens on."
                        >
                            <div className="space-y-5">
                                <Check
                                    name="isRecurring"
                                    label="This event repeats"
                                    hint="It stays a single request, approved once, and appears on the calendar on every date."
                                    checked={formData.isRecurring}
                                    onChange={handleChange}
                                    disabled={isViewOnly}
                                />

                                {formData.isRecurring && (
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
                                        <Field label="Repeats" htmlFor="recurrenceType" className="sm:col-span-2">
                                            <select
                                                name="recurrenceType"
                                                id="recurrenceType"
                                                value={formData.recurrenceType}
                                                onChange={handleChange}
                                                disabled={isViewOnly}
                                            >
                                                <option value="daily">Daily</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="monthly">Monthly</option>
                                            </select>
                                        </Field>

                                        <Field label="Every" htmlFor="recurrenceInterval" className="sm:col-span-2">
                                            <input
                                                type="number"
                                                min="1"
                                                max="30"
                                                name="recurrenceInterval"
                                                id="recurrenceInterval"
                                                value={formData.recurrenceInterval}
                                                onChange={handleChange}
                                                disabled={isViewOnly}
                                            />
                                        </Field>

                                        <Field label="Until" htmlFor="recurrenceEndDate" className="sm:col-span-2">
                                            <input
                                                type="date"
                                                name="recurrenceEndDate"
                                                id="recurrenceEndDate"
                                                min={formData.dayDateOfEvent}
                                                required={formData.isRecurring}
                                                value={formData.recurrenceEndDate}
                                                onChange={handleChange}
                                                disabled={isViewOnly}
                                            />
                                        </Field>

                                        {formData.recurrenceType === 'weekly' && (
                                            <fieldset className="sm:col-span-6">
                                                <legend className="text-sm font-medium text-neutral-800">On these days</legend>
                                                <div className="mt-2 flex flex-wrap gap-4">
                                                    {WEEKDAYS.map((day, index) => (
                                                        <label key={day} className="flex items-center gap-2 text-sm text-neutral-700">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.recurrenceDays.includes(index)}
                                                                onChange={(e) => toggleRecurrenceDay(index, e.target.checked)}
                                                                disabled={isViewOnly}
                                                            />
                                                            {day.slice(0, 3)}
                                                        </label>
                                                    ))}
                                                </div>
                                            </fieldset>
                                        )}

                                        {patternDates.length > 0 && (
                                            <div className="sm:col-span-6 space-y-3">
                                                <Banner tone="info">
                                                    One request on <strong>{occurrences.length}</strong>{' '}
                                                    {occurrences.length === 1 ? 'date' : 'dates'}, approved once.
                                                    {occurrences.length > 0 && (
                                                        <>
                                                            {' '}
                                                            {formatEventDate(occurrences[0])}
                                                            {occurrences.length > 1 &&
                                                                ` through ${formatEventDate(occurrences[occurrences.length - 1])}`}
                                                            .
                                                        </>
                                                    )}
                                                </Banner>

                                                <div>
                                                    <p className="text-sm font-medium text-neutral-800">Dates</p>
                                                    <p className="mt-0.5 text-xs text-neutral-500">
                                                        Click a date to skip it — for a holiday, say — without filing a
                                                        separate request.
                                                    </p>
                                                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                                                        {patternDates.map((date) => {
                                                            const skipped = (formData.skippedDates || []).includes(date);
                                                            return (
                                                                <button
                                                                    key={date}
                                                                    type="button"
                                                                    onClick={() => toggleSkippedDate(date)}
                                                                    disabled={isViewOnly}
                                                                    className={cx(
                                                                        'rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                                                                        skipped
                                                                            ? 'bg-neutral-50 text-neutral-400 line-through ring-neutral-200'
                                                                            : 'bg-white text-neutral-700 ring-neutral-300 hover:bg-neutral-50',
                                                                        isViewOnly && 'cursor-default'
                                                                    )}
                                                                    title={skipped ? 'Skipped — click to put it back' : 'Click to skip this date'}
                                                                >
                                                                    {formatChipDate(date)}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Section>

                        <Section
                            id="slos"
                            title="Learning outcomes"
                            description="Which student learning outcomes this event serves."
                        >
                            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                                <fieldset>
                                    <legend className="sr-only">Student learning outcomes</legend>
                                    <div className="space-y-2">
                                        {SLOS.map((slo) => (
                                            <Check
                                                key={slo.name}
                                                name={slo.name}
                                                label={slo.label}
                                                checked={formData[slo.name]}
                                                onChange={handleChange}
                                                disabled={isViewOnly}
                                            />
                                        ))}
                                    </div>
                                </fieldset>

                                <Field label="How does the event serve them?" htmlFor="sloExplanation">
                                    <textarea
                                        name="sloExplanation"
                                        id="sloExplanation"
                                        rows="8"
                                        placeholder="A sentence or two is enough."
                                        value={formData.sloExplanation}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section
                            id="services"
                            title="Additional services"
                            description="Pick what you need. Each one adds a section below, and pulls in the people who have to approve it."
                        >
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {SERVICES.map((service) => (
                                    <Check
                                        key={service.name}
                                        name={service.name}
                                        label={service.label}
                                        hint={service.hint}
                                        checked={formData[service.name]}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                ))}
                            </div>
                        </Section>

                        {formData.campusLockUp && (
                            <Section id="lockup" title="Campus lock-up">
                                <CampusLockUp
                                    onUpdate={(data) => handleUpdate('campusLockUpForm', data)}
                                    active={formData.campusLockUp}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.campusLockUpForm}
                                />
                            </Section>
                        )}

                        {formData.equipmentRequest && (
                            <Section id="equipment" title="Equipment">
                                <EquipmentRequest
                                    onUpdate={(data) => handleUpdate('equipmentRequestForm', data)}
                                    active={formData.equipmentRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.equipmentRequestForm}
                                />
                            </Section>
                        )}

                        {formData.platformChangeRequest && (
                            <Section id="platform" title="Platform change">
                                <PlatformChangeRequestForm
                                    ref={platformRef}
                                    onUpdate={(data) => handleUpdate('platformChangeRequestForm', data)}
                                    active={formData.platformChangeRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.platformChangeRequestForm}
                                />
                            </Section>
                        )}

                        {formData.liveServicesTechRequest && (
                            <Section id="tech" title="Live services tech">
                                <LiveServicesTechRequestForm
                                    onUpdate={(data) => handleUpdate('liveServicesTechRequestForm', data)}
                                    active={formData.liveServicesTechRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.liveServicesTechRequestForm}
                                />
                            </Section>
                        )}

                        {formData.liveServicesTechRequest && (
                            <Section id="arts" title="Performing arts tech">
                                <PerformingArtsTechRequestForm
                                    onUpdate={(data) => handleUpdate('performingArtsTechRequestForm', data)}
                                    active={formData.liveServicesTechRequest}
                                    isViewOnly={isViewOnly}
                                    initialData={formData.performingArtsTechRequestForm}
                                />
                            </Section>
                        )}

                        {/* Actions ride along at the bottom of a form this long. */}
                        <div className="sticky bottom-4 rounded-lg border border-neutral-200 bg-white px-4 py-3 shadow-lg">
                            {isViewOnly ? (
                                <div className="flex justify-end">
                                    <Button type="button" onClick={() => navigate('/home/request')}>
                                        Back to my requests
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-wrap items-center justify-end gap-3">
                                    {willRestartApprovals && (
                                        <p className="mr-auto text-xs text-neutral-500">
                                            Saving sends this back out for approval.
                                        </p>
                                    )}
                                    <Button type="button" onClick={() => navigate('/home/request')} variant="ghost">
                                        Cancel
                                    </Button>
                                    {!isSubmitted && (
                                        <Button type="button" onClick={handleSaveDraft} disabled={loading}>
                                            Save as draft
                                        </Button>
                                    )}
                                    <Button type="submit" variant="primary" disabled={loading}>
                                        {willRestartApprovals ? 'Save changes & restart approvals' : 'Submit request'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </PageBody>
        </>
    );
};
